import { computed, nextTick, ref, watch } from 'vue';
import { useEmitter } from 'dashboard/composables/emitter';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ContactAPI from 'dashboard/api/contacts';

/**
 * Owns the data layer of the merged interactions thread for one contact.
 *
 * Conversations are fetched from the interactions endpoint (paginated,
 * newest-first) and hydrated into the shared conversation store, so the thread
 * renders straight from `allConversations` and every existing message action
 * (delete, retry, translate, …) and websocket update works natively. The
 * composable only owns the ordered id list, pagination, and scroll behaviour.
 *
 * @param {import('vue').Ref<number>} contactId - The selected contact id.
 * @param {import('vue').Ref<HTMLElement|null>} scrollContainer - Thread scroll element.
 */
export function useContactInteractions(contactId, scrollContainer) {
  const store = useStore();
  const getConversationById = useMapGetter('getConversationById');

  // Conversation display ids in render order (oldest-first, newest at bottom).
  const conversationIds = ref([]);
  const messagesCountById = ref({});
  const meta = ref({ currentPage: 1, totalPages: 1, count: 0 });
  const isFetching = ref(false);
  const isLoadingOlder = ref(false);
  const hasError = ref(false);
  // Prevents the scroll handler from paginating before the initial
  // scroll-to-bottom has settled (otherwise it opens at the oldest message).
  const readyForOlder = ref(false);

  // Bumped on every contact switch so slow responses never cross-populate.
  let requestToken = 0;

  const conversations = computed(() =>
    conversationIds.value
      .map(id => getConversationById.value(id))
      .filter(Boolean)
  );
  const hasConversations = computed(() => conversations.value.length > 0);
  const hasOlderConversations = computed(
    () => meta.value.currentPage < meta.value.totalPages
  );
  const messagesCount = id => messagesCountById.value[id] || 0;

  const isNearBottom = () => {
    const el = scrollContainer.value;
    if (!el) return false;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 160;
  };

  // Pin to the newest message. Re-asserted across frames because message
  // bubbles (and images) can grow after the first paint, which would otherwise
  // leave the view stranded near the top.
  const scrollToBottom = async () => {
    await nextTick();
    const stick = () => {
      const el = scrollContainer.value;
      if (el) el.scrollTop = el.scrollHeight;
    };
    stick();
    requestAnimationFrame(() => {
      stick();
      requestAnimationFrame(stick);
    });
  };

  const applyMeta = data => {
    meta.value = {
      currentPage: data.meta?.current_page || 1,
      totalPages: data.meta?.total_pages || 1,
      count: data.meta?.count || 0,
    };
  };

  const indexMessagesCount = payload => {
    payload.forEach(conversation => {
      messagesCountById.value[conversation.id] = conversation.messages_count;
    });
  };

  const loadInitial = async () => {
    requestToken += 1;
    const token = requestToken;
    conversationIds.value = [];
    messagesCountById.value = {};
    hasError.value = false;
    readyForOlder.value = false;
    if (!contactId.value) return;

    isFetching.value = true;
    try {
      const { data } = await ContactAPI.getInteractions(contactId.value, 1);
      if (token !== requestToken) return;
      const payload = data.payload || [];
      store.dispatch('hydrateConversations', payload);
      indexMessagesCount(payload);
      conversationIds.value = payload.map(c => c.id).reverse();
      applyMeta(data);
      await scrollToBottom();
      readyForOlder.value = true;
    } catch (error) {
      if (token === requestToken) hasError.value = true;
    } finally {
      if (token === requestToken) isFetching.value = false;
    }
  };

  const loadOlder = async () => {
    if (
      !readyForOlder.value ||
      !hasOlderConversations.value ||
      isLoadingOlder.value ||
      isFetching.value
    ) {
      return;
    }
    const token = requestToken;
    isLoadingOlder.value = true;
    try {
      const nextPage = meta.value.currentPage + 1;
      const { data } = await ContactAPI.getInteractions(
        contactId.value,
        nextPage
      );
      if (token !== requestToken) return;
      const payload = data.payload || [];
      const el = scrollContainer.value;
      const previousHeight = el ? el.scrollHeight : 0;
      store.dispatch('hydrateConversations', payload);
      indexMessagesCount(payload);
      conversationIds.value = [
        ...payload.map(c => c.id).reverse(),
        ...conversationIds.value,
      ];
      meta.value.currentPage = data.meta?.current_page || nextPage;
      // Keep the viewport anchored on the same message after prepending.
      await nextTick();
      if (el) el.scrollTop += el.scrollHeight - previousHeight;
    } catch (error) {
      // Recoverable: the user can scroll or use the load-earlier button again.
    } finally {
      isLoadingOlder.value = false;
    }
  };

  const loadingEarlierIds = ref(new Set());
  const isLoadingEarlier = id => loadingEarlierIds.value.has(id);

  // Pull the next page of older messages for a single conversation into the
  // store (same action the live view uses when scrolling up), keeping the
  // thread anchored so the block grows upward without the view jumping.
  const loadEarlierMessages = async conversation => {
    const before = conversation.messages?.[0]?.id;
    if (!before || isLoadingEarlier(conversation.id)) return;
    loadingEarlierIds.value = new Set(loadingEarlierIds.value).add(
      conversation.id
    );
    const el = scrollContainer.value;
    const previousHeight = el ? el.scrollHeight : 0;
    try {
      await store.dispatch('fetchPreviousMessages', {
        conversationId: conversation.id,
        before,
      });
      // Keep the viewport anchored so the block grows upward without jumping,
      // which also scrolls the trigger out of view until the user scrolls again.
      await nextTick();
      if (el) el.scrollTop += el.scrollHeight - previousHeight;
    } finally {
      const next = new Set(loadingEarlierIds.value);
      next.delete(conversation.id);
      loadingEarlierIds.value = next;
    }
  };

  const onMessageCreated = data => {
    if (!data || !conversationIds.value.includes(data.conversation_id)) return;
    // The store appends the message natively; we only own keeping the view
    // pinned to the bottom when the user is already there.
    if (isNearBottom()) scrollToBottom();
  };

  const onConversationCreated = data => {
    if (!contactId.value || data?.meta?.sender?.id !== contactId.value) return;
    if (conversationIds.value.includes(data.id)) return;
    store.dispatch('hydrateConversations', [data]);
    messagesCountById.value[data.id] = (data.messages || []).length;
    const wasNearBottom = isNearBottom();
    conversationIds.value = [...conversationIds.value, data.id];
    meta.value.count += 1;
    if (wasNearBottom) scrollToBottom();
  };

  useEmitter(BUS_EVENTS.MESSAGE_CREATED, onMessageCreated);
  useEmitter(BUS_EVENTS.CONVERSATION_CREATED, onConversationCreated);

  watch(contactId, loadInitial, { immediate: true });

  return {
    conversations,
    meta,
    isFetching,
    isLoadingOlder,
    hasError,
    hasOlderConversations,
    hasConversations,
    messagesCount,
    isLoadingEarlier,
    loadInitial,
    loadOlder,
    loadEarlierMessages,
  };
}
